import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ShieldCheck, Lock, CheckCircle, ArrowRight, FileText, Database, Package,
  Warning, Certificate,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Public Portfolio Request Portal — /portfolio
 *
 *  External partners (regulatory teams, retail buyers, due-diligence
 *  consultancies, investors) submit a controlled request to access Aᴳ
 *  intelligence portfolios. This page exposes NO underlying data — only the
 *  catalog of what can be requested and the form to request it.
 *
 *  Architecture intent (visible to user): every request is admin-reviewed,
 *  scope-confirmed, priced, and only then delivered through controlled
 *  channels (signed-URL PDF / scoped API token in later phases).
 * =======================================================================*/

const SECURITY_PILLARS = [
  { icon: Lock,          title: 'No raw database access',     desc: 'Curated portfolios only. No full table exports. No unrestricted crawling.' },
  { icon: ShieldCheck,   title: 'Toxicology logic protected', desc: 'Scoring methodology, exposure logic, and Prop 65 modeling remain proprietary.' },
  { icon: Certificate,   title: 'Admin-approved delivery',    desc: 'Every request is reviewed by an Aᴳ regulatory lead before fulfillment.' },
  { icon: FileText,      title: 'Audit-logged & signed-URL',  desc: 'Tokenized API access · single-use download links · row/quota limits · full audit trail.' },
];

export default function PortfolioRequestPage() {
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState('intro');  // intro | form | success
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    organization: '',
    contact_name: '',
    contact_email: '',
    contact_title: '',
    industry: '',
    portfolio_category: 'ingredient',
    portfolio_types: [],
    jurisdictions: [],
    tier: 'standard',
    access_type: 'downloadable_report',
    intended_use: '',
    target_volume: '',
    timeline: '',
    additional_notes: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/portfolio/catalog`);
        setCatalog(r.data);
        if (r.data.industries?.length) setForm(f => ({ ...f, industry: r.data.industries[0] }));
      } catch (e) { setError('Unable to load portfolio catalog. Please try again later.'); }
    })();
  }, []);

  const portfolioOptions = useMemo(() => {
    if (!catalog) return [];
    return form.portfolio_category === 'ingredient'
      ? catalog.ingredient_portfolios
      : catalog.packaging_portfolios;
  }, [catalog, form.portfolio_category]);

  const togglePortfolio = (id) => setForm(f => ({
    ...f,
    portfolio_types: f.portfolio_types.includes(id) ? f.portfolio_types.filter(p => p !== id) : [...f.portfolio_types, id],
  }));
  const toggleJurisdiction = (j) => setForm(f => ({
    ...f,
    jurisdictions: f.jurisdictions.includes(j) ? f.jurisdictions.filter(p => p !== j) : [...f.jurisdictions, j],
  }));

  // Reset portfolio_types when category flips
  useEffect(() => { setForm(f => ({ ...f, portfolio_types: [] })); }, [form.portfolio_category]);

  const validate = () => {
    if (!form.organization || form.organization.length < 2) return 'Organization is required.';
    if (!form.contact_name || form.contact_name.length < 2) return 'Contact name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) return 'A valid contact email is required.';
    if (form.portfolio_types.length === 0) return 'Select at least one portfolio.';
    if (form.jurisdictions.length === 0) return 'Select at least one jurisdiction.';
    if (!form.intended_use || form.intended_use.length < 10) return 'Please describe the intended use (≥10 chars).';
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(''); setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/portfolio/request`, form);
      setSuccess(r.data);
      setStep('success');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  /* ===================== INTRO STEP ===================== */
  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-[#0B1426] text-white">
        <header className="border-b border-white/10 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white" data-testid="portfolio-home-link">
              <span className="text-xl font-bold tracking-tight">A<sup className="text-[11px] font-bold tracking-normal">G</sup></span>
              <span className="text-xs uppercase tracking-[0.3em] text-white/60 hidden sm:inline">Portfolio Access</span>
            </Link>
            <Link to="/" className="text-xs text-white/60 hover:text-white">← Back to ToxIntel</Link>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#FF4E6A] mb-3">Aᴳ Regulatory Intelligence · Licensed Access</p>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
            Controlled commercial access<br />to Aᴳ regulatory portfolios.
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed mb-8">
            Curated, jurisdiction-specific intelligence portfolios on restricted ingredients, packaging compliance,
            Prop 65 surveillance, fragrance allergens, and retailer-restricted lists. Available under license to
            regulatory teams, retail buyers, due-diligence consultancies, and investors.
          </p>

          {/* Security pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 max-w-4xl">
            {SECURITY_PILLARS.map(p => (
              <div key={p.title} className="border border-white/15 rounded p-4 bg-white/[0.03]">
                <p.icon size={20} className="text-[#FF4E6A] mb-2" weight="duotone" />
                <p className="text-sm font-semibold mb-1">{p.title}</p>
                <p className="text-[12px] text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setStep('form')}
              className="h-11 px-6 text-sm bg-[#FF4E6A] hover:bg-[#E63E5A] text-white rounded font-semibold"
              data-testid="portfolio-start-request"
            >
              Request a portfolio <ArrowRight size={16} className="ml-2" weight="bold" />
            </Button>
            <a href="mailto:regulatory@tagc.us" className="inline-flex items-center text-sm text-white/70 hover:text-white h-11 px-4">
              Or email regulatory@tagc.us
            </a>
          </div>

          {/* What's available */}
          {catalog && (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-white/10 rounded p-5 bg-white/[0.02]">
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-3">
                  <Database size={14} weight="duotone" className="text-[#FF4E6A]" /> Ingredient Intelligence Portfolios
                </p>
                <ul className="space-y-1.5 text-[13px] text-white/80">
                  {catalog.ingredient_portfolios.map(p => (
                    <li key={p.id} className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-[#FF4E6A]" /> {p.label}</li>
                  ))}
                </ul>
              </div>
              <div className="border border-white/10 rounded p-5 bg-white/[0.02]">
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-3">
                  <Package size={14} weight="duotone" className="text-[#FF4E6A]" /> Packaging Compliance & RSL Portfolios
                </p>
                <ul className="space-y-1.5 text-[13px] text-white/80">
                  {catalog.packaging_portfolios.map(p => (
                    <li key={p.id} className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-[#FF4E6A]" /> {p.label}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Workflow strip */}
          <div className="mt-14 border-t border-white/10 pt-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 mb-4">Commercial workflow</p>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-[12px]">
              {['Request submitted', 'Aᴳ admin reviews', 'Scope + pricing proposal', 'Terms accepted', 'Signed-URL delivery / API token'].map((s, i) => (
                <div key={i} className="border border-white/10 rounded p-3 bg-white/[0.02]">
                  <span className="text-[#FF4E6A] font-black tabular-nums">0{i + 1}</span>
                  <p className="mt-1 text-white/80">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* ===================== SUCCESS STEP ===================== */
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-[#0B1426] text-white flex flex-col">
        <header className="border-b border-white/10 px-6 py-4">
          <div className="max-w-7xl mx-auto"><Link to="/" className="text-xl font-bold">A<sup className="text-[11px] font-bold">G</sup></Link></div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="max-w-xl text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FF4E6A]/20 border border-[#FF4E6A]/40 flex items-center justify-center">
              <CheckCircle size={28} className="text-[#FF4E6A]" weight="fill" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Request received</h1>
            <p className="text-white/70 mb-4">{success?.message}</p>
            <p className="text-[12px] text-white/60 mb-6">Reference: <span className="font-mono text-white" data-testid="portfolio-success-ref">{success?.ref}</span></p>
            <Link to="/"><Button variant="outline" className="rounded border-white/40 text-white hover:bg-white hover:text-[#0B1426]" data-testid="portfolio-success-home">← Back to ToxIntel</Button></Link>
          </div>
        </main>
      </div>
    );
  }

  /* ===================== FORM STEP ===================== */
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-[#0B1426] text-white px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight">A<sup className="text-[11px] font-bold tracking-normal">G</sup> <span className="text-xs uppercase tracking-[0.3em] text-white/60 hidden sm:inline ml-1">Portfolio Access</span></Link>
          <button onClick={() => setStep('intro')} className="text-[12px] text-white/60 hover:text-white" data-testid="portfolio-back-to-intro">← Cancel</button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8 lg:py-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF4E6A] mb-2">Portfolio Request</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#111827] mb-6">Tell us what you need</h1>

        <div className="space-y-6">
          {/* Contact */}
          <section className="card-flat p-5 bg-white">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-3">Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Organization *</Label>
                <Input value={form.organization} onChange={(e) => setForm(f => ({ ...f, organization: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="Mind the Chain" data-testid="portfolio-organization" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Industry *</Label>
                <select value={form.industry} onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="portfolio-industry">
                  {catalog?.industries?.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Contact name *</Label>
                <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="portfolio-contact-name" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Title</Label>
                <Input value={form.contact_title} onChange={(e) => setForm(f => ({ ...f, contact_title: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="VP Regulatory" data-testid="portfolio-contact-title" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Contact email *</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="portfolio-contact-email" />
              </div>
            </div>
          </section>

          {/* Portfolio category + types */}
          <section className="card-flat p-5 bg-white">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-3">Portfolio scope</h2>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Category *</Label>
            <div className="mt-1 grid grid-cols-2 gap-2 mb-4">
              {['ingredient', 'packaging'].map(c => (
                <button key={c} type="button" onClick={() => setForm(f => ({ ...f, portfolio_category: c }))} className={`text-left p-3 rounded border ${form.portfolio_category === c ? 'border-[#FF4E6A] bg-[#FFE9EE]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid={`portfolio-cat-${c}`}>
                  <p className="text-sm font-bold text-[#111827] capitalize">{c} portfolios</p>
                  <p className="text-[11px] text-[#4B5563] mt-0.5">{c === 'ingredient' ? '8 portfolio types · ingredient intelligence' : '6 portfolio types · packaging compliance & RSL'}</p>
                </button>
              ))}
            </div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Portfolios * (select all that apply)</Label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-4">
              {portfolioOptions.map(p => {
                const sel = form.portfolio_types.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => togglePortfolio(p.id)} className={`text-left text-[12px] px-3 py-2 rounded border ${sel ? 'border-[#FF4E6A] bg-[#FFE9EE] text-[#111827]' : 'border-gray-200 bg-white text-[#4B5563] hover:border-gray-400'}`} data-testid={`portfolio-type-${p.id}`}>
                    <p className="font-semibold">{p.label}</p>
                  </button>
                );
              })}
            </div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Jurisdictions * (select all that apply)</Label>
            <div className="mt-1 flex flex-wrap gap-1.5 mb-4">
              {catalog?.jurisdictions?.map(j => {
                const sel = form.jurisdictions.includes(j);
                return (
                  <button key={j} type="button" onClick={() => toggleJurisdiction(j)} className={`text-[11px] px-2.5 py-1 rounded border ${sel ? 'border-[#FF4E6A] bg-[#FFE9EE]' : 'border-gray-200 bg-white text-[#4B5563] hover:border-gray-400'}`} data-testid={`portfolio-juris-${j}`}>{j}</button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Tier *</Label>
                <select value={form.tier} onChange={(e) => setForm(f => ({ ...f, tier: e.target.value }))} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="portfolio-tier">
                  {catalog?.tiers?.map(t => <option key={t.id} value={t.id}>{t.label} · {t.desc}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Access *</Label>
                <select value={form.access_type} onChange={(e) => setForm(f => ({ ...f, access_type: e.target.value }))} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="portfolio-access">
                  <option value="downloadable_report">Downloadable report (signed-URL)</option>
                  <option value="api_access">Scoped API token</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
          </section>

          {/* Use */}
          <section className="card-flat p-5 bg-white">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-3">Use case</h2>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Intended use * (min 10 chars)</Label>
            <Textarea value={form.intended_use} onChange={(e) => setForm(f => ({ ...f, intended_use: e.target.value }))} rows={3} className="text-[12px] mt-1 mb-3" placeholder="e.g. Pre-purchase due diligence on private label cosmetics; quarterly regulatory surveillance for our 14-brand portfolio." data-testid="portfolio-intended-use" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Target volume / coverage</Label>
                <Input value={form.target_volume} onChange={(e) => setForm(f => ({ ...f, target_volume: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="e.g. ~500 SKUs / qtr" data-testid="portfolio-target-volume" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Timeline</Label>
                <Input value={form.timeline} onChange={(e) => setForm(f => ({ ...f, timeline: e.target.value }))} className="mt-1 h-9 text-sm" placeholder="e.g. need by Q3 2026" data-testid="portfolio-timeline" />
              </div>
            </div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mt-3 block">Additional notes</Label>
            <Textarea value={form.additional_notes} onChange={(e) => setForm(f => ({ ...f, additional_notes: e.target.value }))} rows={2} className="text-[12px] mt-1" data-testid="portfolio-notes" />
          </section>

          {error && (
            <div className="bg-[#FDE8E8] border border-[#F8B4B4] text-[#9B1C1C] rounded p-3 text-sm flex items-start gap-2" data-testid="portfolio-error">
              <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {/* Confidentiality strip */}
          <p className="text-[11px] text-[#4B5563] italic flex items-start gap-1.5 max-w-3xl">
            <Lock size={12} weight="fill" className="flex-shrink-0 mt-0.5 text-[#4B5563]" />
            By submitting this request you acknowledge that all Aᴳ portfolios are delivered under license; no raw database access, no exposure of scoring methodology or toxicology logic, and no redistribution. Every request is reviewed by an Aᴳ regulatory lead before fulfillment.
          </p>

          <Button onClick={submit} disabled={submitting} className="w-full sm:w-auto h-11 px-8 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold" data-testid="portfolio-submit">
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </main>
    </div>
  );
}
